// Module 01 (domain) — the ONE loading-band ladder + the demand/voltage stress classifiers. Both the engine's
// wall/parcel status (demand-model.loadRuntimes) and the viz node-point/facility-parcel status (effects/assets.ts)
// read this so a co-located node and wall never disagree (C44). Breakpoints grounded: shortage=100 == NERC
// continuous facility rating (FAC-008); stressed=80 == pre-limit alert; elevated=55 == normal-peak-hour band.
// Demand bands from a served-demand-vs-base ramp; voltage sag from ANSI C84.1 Range A (0.95 pu).
// Pure, deterministic (+−*/ and compares only). DISPLAY-ONLY: nothing here changes a dispatched MW — status labels
// only (golden-neutral by construction). source: NERC TPL-001-5.1 / FAC-008-5 / ANSI C84.1-2020. as_of: 2026-07-04.

export const LOADING_BANDS = { elevated: 55, stressed: 80, shortage: 100 } as const; // % of rating
export const DEMAND_BANDS = { elevated: 1.15, stressed: 1.30 } as const; // served-demand / base-hour reference
export const VOLTAGE_SAG_PU = 0.95; // ANSI C84.1 Range A lower bound

export type LoadBand = "normal" | "elevated" | "stressed" | "shortage";

export function loadBand(loadingPct: number): LoadBand {
  if (loadingPct >= LOADING_BANDS.shortage) return "shortage";
  if (loadingPct >= LOADING_BANDS.stressed) return "stressed";
  if (loadingPct >= LOADING_BANDS.elevated) return "elevated";
  return "normal";
}

export function demandBand(loadFactor: number): LoadBand {
  if (loadFactor >= DEMAND_BANDS.stressed) return "stressed";
  if (loadFactor >= DEMAND_BANDS.elevated) return "elevated";
  return "normal";
}

// P7 LinDistFlow populates busVMagPu every tick; undefined ⇒ no voltage signal (returns "normal").
export function voltageBand(vMagPu: number | undefined): LoadBand {
  return vMagPu !== undefined && vMagPu < VOLTAGE_SAG_PU ? "stressed" : "normal";
}

const BAND_ORDER: Record<LoadBand, number> = { normal: 0, elevated: 1, stressed: 2, shortage: 3 };

// Pick the WORSE (highest-severity) of the three condition inputs — incident loading, demand pressure, voltage sag.
// Called positionally with exactly 3 args (no rest-array alloc on the hot path).
export function worseOf(a: LoadBand, b: LoadBand, c: LoadBand): LoadBand {
  return BAND_ORDER[b] > BAND_ORDER[a] ? (BAND_ORDER[c] > BAND_ORDER[b] ? c : b) : (BAND_ORDER[c] > BAND_ORDER[a] ? c : a);
}
